import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DesktopOutlined,
  EyeOutlined,
  FileImageOutlined,
  MobileOutlined,
  RollbackOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  BlogPostStatus,
  blogImageExtensions,
  blogImageMaxBytes,
  createBlogPost,
  deleteBlogCoverImage,
  deleteBlogPost,
  getBlogPost,
  listBlogCategories,
  listBlogPosts,
  publishBlogPost,
  unpublishBlogPost,
  updateBlogPost,
  uploadBlogCoverImage,
  websiteBlogBaseUrl,
} from '../api/blog';
import type { BlogCategory, BlogPost, BlogPostSummary, SaveBlogPostPayload } from '../api/blog';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import { BlogCategoryModal } from './blog/BlogCategoryModal';
import { RichTextEditor } from './blog/RichTextEditor';
import { SeoPanel } from './blog/SeoPanel';
import { formatDateTime, postStatusTag } from './blog/blogDisplay';
import { BlogArticlePreview } from './blog/preview/BlogArticlePreview';
import { WebsiteFrame } from './blog/preview/WebsiteFrame';
import type { PreviewDevice } from './blog/preview/WebsiteFrame';
import { fromSummary, statusOf } from './blog/preview/previewModel';
import type { PreviewArticle, PreviewCategory, PreviewPost } from './blog/preview/previewModel';
import { runSeoChecks, slugify } from './blog/seoChecks';
import { BrandColors } from '../theme/brand';

/** Các trường của biểu mẫu. Nội dung bài và ảnh bìa không nằm ở đây: chúng giữ trong trạng thái riêng. */
interface EditorFormValues {
  readonly title: string;
  readonly slug: string | undefined;
  readonly excerpt: string | undefined;
  readonly categoryId: string | undefined;
  readonly authorName: string | undefined;
  readonly seoTitle: string | undefined;
  readonly metaDescription: string | undefined;
  readonly focusKeyword: string | undefined;
  readonly coverImageAlt: string | undefined;
}

/** Việc người viết muốn làm khi bấm nút: chỉ lưu, đăng (hoặc cập nhật bài đang đăng), hay gỡ về nháp. */
type SaveIntent = 'save' | 'publish' | 'unpublish';

/** Đường dẫn trang sửa một bài. */
function editRoute(id: string): string {
  return AppRoutes.BlogEditPattern.replace(':postId', id);
}

/**
 * Giá trị trường đang theo dõi, rỗng khi chưa có. Kiểu khai báo nói trường luôn có giá trị, nhưng lúc biểu mẫu
 * vừa dựng thì nó vẫn là undefined.
 */
function orEmpty(value: string | undefined): string {
  return value ?? '';
}

/** Bỏ khoảng trắng; chuỗi rỗng thành null. */
function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

/**
 * Trang viết và sửa một bài blog.
 *
 * <b>Bài mới luôn lưu ở dạng nháp trước</b>, rồi mới đăng — đăng là một bước riêng, có thể chọn ngày đăng
 * ở tương lai để hẹn giờ. Ảnh bìa chọn xong chỉ gửi đi khi bấm lưu, sau khi bài đã có định danh.
 */
export function BlogPostEditorPage(): ReactNode {
  const { postId } = useParams<{ readonly postId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.BlogManage);

  const [form] = Form.useForm<EditorFormValues>();
  const slug = Form.useWatch('slug', form);
  const watchedTitle = Form.useWatch('title', form);
  const watchedExcerpt = Form.useWatch('excerpt', form);
  const watchedSeoTitle = Form.useWatch('seoTitle', form);
  const watchedMetaDescription = Form.useWatch('metaDescription', form);
  const watchedFocusKeyword = Form.useWatch('focusKeyword', form);
  const watchedCoverImageAlt = Form.useWatch('coverImageAlt', form);
  const watchedCategoryId = Form.useWatch('categoryId', form);
  const watchedAuthorName = Form.useWatch('authorName', form);

  const [post, setPost] = useState<BlogPost | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Trình soạn thảo chỉ đọc nội dung ban đầu một lần; đổi "hạt giống" là dựng lại nó với nội dung mới.
  const [editorSeed, setEditorSeed] = useState(0);
  const [initialHtml, setInitialHtml] = useState('');
  const [contentHtml, setContentHtml] = useState('');

  const [publishAt, setPublishAt] = useState<Dayjs | null>(null);
  const [savingIntent, setSavingIntent] = useState<SaveIntent | null>(null);

  const [categories, setCategories] = useState<readonly BlogCategory[]>([]);
  const [categoryToken, setCategoryToken] = useState(0);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  // Xem trước trên website: bài đang soạn đặt giữa các bài đã đăng, đúng như khách sẽ thấy.
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [publishedPosts, setPublishedPosts] = useState<readonly BlogPostSummary[]>([]);

  // Ảnh bìa đang chọn — chưa gửi đi cho tới lúc bấm lưu.
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [isCoverRemoved, setIsCoverRemoved] = useState(false);

  const isNew = postId === undefined;
  const isReady = isNew || post?.id === postId;

  const applyPost = useCallback(
    (loadedPost: BlogPost, resetEditor: boolean) => {
      setPost(loadedPost);
      form.setFieldsValue({
        title: loadedPost.title,
        slug: loadedPost.slug,
        excerpt: loadedPost.excerpt,
        categoryId: loadedPost.categoryId ?? undefined,
        authorName: loadedPost.authorName ?? undefined,
        seoTitle: loadedPost.seoTitle ?? undefined,
        metaDescription: loadedPost.metaDescription ?? undefined,
        focusKeyword: loadedPost.focusKeyword ?? undefined,
        coverImageAlt: loadedPost.coverImageAlt ?? undefined,
      });
      setPublishAt(loadedPost.publishedAt === null ? null : dayjs(loadedPost.publishedAt));

      if (resetEditor) {
        setInitialHtml(loadedPost.contentHtml);
        setContentHtml(loadedPost.contentHtml);
        setEditorSeed((seed) => seed + 1);
      }
    },
    [form],
  );

  // Nạp bài khi mở trang sửa. Bỏ qua khi bài vừa được tạo ngay trên trang này — đã có sẵn trong trạng thái.
  useEffect(() => {
    if (postId === undefined || post?.id === postId) {
      return;
    }

    const id: string = postId;
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const loadedPost = await getBlogPost(id);

        if (isCurrent) {
          setLoadError(null);
          applyPost(loadedPost, true);
        }
      } catch (error) {
        if (isCurrent) {
          setLoadError(error instanceof ApiError ? error.message : 'Không tải được bài viết.');
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [postId, post?.id, applyPost]);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const result = await listBlogCategories();

        if (isCurrent) {
          setCategories(result);
        }
      } catch {
        // Thiếu danh mục thì ô chọn danh mục trống; vẫn viết bài được.
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [categoryToken]);

  // Ảnh xem trước là một địa chỉ tạm trong bộ nhớ trình duyệt; phải trả lại khi không dùng nữa.
  useEffect(() => {
    return () => {
      if (coverPreview !== null) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  const shownCoverUrl = coverPreview ?? (isCoverRemoved ? null : (post?.coverImageUrl ?? null));

  // Những gì sẽ thật sự lên Google: trường SEO nếu có, không thì tiêu đề bài và tóm tắt — đúng cách máy chủ
  // điền sẵn cho website.
  const effectiveSlug = slugify(emptyToNull(slug) ?? orEmpty(watchedTitle));
  const effectiveTitle = emptyToNull(watchedSeoTitle) ?? orEmpty(watchedTitle).trim();
  const effectiveDescription = emptyToNull(watchedMetaDescription) ?? (watchedExcerpt ?? '').trim();

  const seoReport = useMemo(
    () =>
      runSeoChecks({
        title: orEmpty(watchedTitle),
        seoTitle: watchedSeoTitle ?? '',
        metaDescription: watchedMetaDescription ?? '',
        excerpt: watchedExcerpt ?? '',
        slug: effectiveSlug,
        focusKeyword: watchedFocusKeyword ?? '',
        contentHtml,
        hasCoverImage: shownCoverUrl !== null,
        coverImageAlt: watchedCoverImageAlt ?? '',
      }),
    [
      watchedTitle,
      watchedSeoTitle,
      watchedMetaDescription,
      watchedExcerpt,
      effectiveSlug,
      watchedFocusKeyword,
      contentHtml,
      shownCoverUrl,
      watchedCoverImageAlt,
    ],
  );

  function resetCover(): void {
    setCoverFile(null);
    setCoverPreview(null);
    setIsCoverRemoved(false);
  }

  function pickCover(file: File): boolean {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!(blogImageExtensions as readonly string[]).includes(extension)) {
      message.error(`Ảnh bìa chỉ nhận tệp ${blogImageExtensions.join(', ')}.`);
    } else if (file.size > blogImageMaxBytes) {
      message.error('Ảnh bìa tối đa 5 MB.');
    } else {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
      setIsCoverRemoved(false);
    }

    // Luôn chặn phép tải lên mặc định: ảnh chỉ được gửi khi bấm lưu, kèm phiếu xác thực.
    return false;
  }

  /** Gửi thay đổi ảnh bìa, nếu có. Hỏng thì báo riêng — phần bài viết đã lưu xong. */
  async function saveCover(saved: BlogPost): Promise<BlogPost> {
    try {
      if (coverFile !== null) {
        const result = await uploadBlogCoverImage(saved.id, coverFile);

        resetCover();

        return result;
      }

      if (isCoverRemoved && saved.coverImageUrl !== null) {
        const result = await deleteBlogCoverImage(saved.id);

        resetCover();

        return result;
      }
    } catch (error) {
      message.warning(
        `Đã lưu bài, nhưng chưa lưu được ảnh bìa — ${error instanceof ApiError ? error.message : 'lỗi không xác định'}.`,
        8,
      );
    }

    return saved;
  }

  async function save(intent: SaveIntent): Promise<void> {
    let values: EditorFormValues;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    if (intent === 'publish') {
      if (emptyToNull(values.excerpt) === null) {
        form.setFields([{ name: 'excerpt', errors: ['Bài đăng lên website phải có tóm tắt.'] }]);

        return;
      }

      if (contentHtml === '') {
        message.error('Bài đăng lên website phải có nội dung.');

        return;
      }
    }

    const payload: SaveBlogPostPayload = {
      title: values.title,
      slug: emptyToNull(values.slug),
      excerpt: values.excerpt?.trim() ?? '',
      contentHtml,
      categoryId: values.categoryId ?? null,
      authorName: emptyToNull(values.authorName),
      seoTitle: emptyToNull(values.seoTitle),
      metaDescription: emptyToNull(values.metaDescription),
      focusKeyword: emptyToNull(values.focusKeyword),
      coverImageAlt: emptyToNull(values.coverImageAlt),
    };

    setSavingIntent(intent);

    try {
      let saved = post === null ? await createBlogPost(payload) : await updateBlogPost(post.id, payload);

      applyPost(saved, false);

      if (post === null) {
        void navigate(editRoute(saved.id), { replace: true });
      }

      saved = await saveCover(saved);

      if (intent === 'publish') {
        saved = await publishBlogPost(saved.id, publishAt?.toISOString() ?? null);
      } else if (intent === 'unpublish') {
        saved = await unpublishBlogPost(saved.id);
      }

      applyPost(saved, false);

      if (intent === 'publish') {
        message.success(saved.isScheduled ? 'Đã hẹn giờ đăng bài.' : 'Đã đăng bài lên website.');
      } else if (intent === 'unpublish') {
        message.success('Đã gỡ bài về nháp.');
      } else {
        message.success('Đã lưu.');
      }
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được bài viết.');
    } finally {
      setSavingIntent(null);
    }
  }

  async function openPreview(): Promise<void> {
    setIsPreviewOpen(true);

    try {
      const page = await listBlogPosts({ pageNumber: 1, pageSize: 50, status: BlogPostStatus.Published });

      setPublishedPosts(page.items);
    } catch {
      // Thiếu các bài khác thì bản xem trước chỉ có bài đang soạn — vẫn xem được.
    }
  }

  /** Bài đang soạn, dựng thành dạng xem trước — kể cả những gì chưa lưu. */
  function draftArticle(): PreviewArticle {
    const category = categories.find((candidate) => candidate.id === watchedCategoryId);
    const title = orEmpty(watchedTitle).trim();
    const isFuture = publishAt?.isAfter(dayjs()) === true;

    return {
      id: post?.id ?? 'bai-dang-soan',
      slug: effectiveSlug,
      title: title === '' ? 'Tiêu đề bài viết' : title,
      excerpt: orEmpty(watchedExcerpt),
      coverImageUrl: shownCoverUrl,
      coverImageAlt: emptyToNull(watchedCoverImageAlt) ?? title,
      categoryName: category?.name ?? null,
      categorySlug: category?.slug ?? null,
      authorName: emptyToNull(watchedAuthorName),
      date: publishAt?.toISOString() ?? post?.publishedAt ?? null,
      status: post === null ? 'draft' : post.status === BlogPostStatus.Published ? (isFuture ? 'scheduled' : statusOf(post)) : 'draft',
      contentHtml,
    };
  }

  /** Các bài khách đang thấy, không gồm bài đang soạn — làm cột bên và mục "bài cùng danh mục". */
  const otherPosts: readonly PreviewPost[] = publishedPosts
    .filter((candidate) => !candidate.isScheduled && candidate.id !== post?.id)
    .map((candidate) => fromSummary(candidate, categories));

  function previewCategories(article: PreviewArticle): readonly PreviewCategory[] {
    const all = [...otherPosts, article];

    return categories
      .map((category) => ({
        name: category.name,
        slug: category.slug,
        count: all.filter((candidate) => candidate.categorySlug === category.slug).length,
      }))
      .filter((category) => category.count > 0);
  }

  async function remove(): Promise<void> {
    if (post === null) {
      return;
    }

    try {
      await deleteBlogPost(post.id);
      message.success('Đã xóa bài viết.');
      void navigate(AppRoutes.Blog);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  const isPublished = post?.status === BlogPostStatus.Published;
  const isFuture = publishAt?.isAfter(dayjs()) === true;
  const isBusy = savingIntent !== null;

  function statusLine(): ReactNode {
    if (post === null || post.status === BlogPostStatus.Draft) {
      return 'Bản nháp — chỉ người trong ERP thấy.';
    }

    return post.isScheduled
      ? `Hẹn giờ: tự hiện trên website lúc ${formatDateTime(post.publishedAt)}.`
      : `Đang hiện trên website từ ${formatDateTime(post.publishedAt)}.`;
  }

  if (loadError !== null) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button type="link" icon={<ArrowLeftOutlined />} style={{ paddingInline: 0 }} onClick={() => { void navigate(AppRoutes.Blog); }}>
          Danh sách bài viết
        </Button>
        <Alert type="error" showIcon message={loadError} />
      </Space>
    );
  }

  return (
    <Spin spinning={!isReady}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Space size={12} align="center">
            <Button
              icon={<ArrowLeftOutlined />}
              aria-label="Về danh sách bài viết"
              onClick={() => { void navigate(AppRoutes.Blog); }}
            />
            <Typography.Title level={4} style={{ margin: 0 }}>
              {isNew ? 'Viết bài mới' : 'Sửa bài viết'}
            </Typography.Title>
            {post === null ? null : postStatusTag(post)}
          </Space>
          <Button icon={<EyeOutlined />} onClick={() => { void openPreview(); }}>
            Xem trước trên website
          </Button>
        </div>

        <Form form={form} layout="vertical" requiredMark={false} disabled={!canManage}>
          <Row gutter={[16, 16]} align="top">
            <Col xs={24} xl={16} xxl={17}>
              <Card>
                <Form.Item
                  name="title"
                  label="Tiêu đề"
                  rules={[{ required: true, whitespace: true, message: 'Chưa nhập tiêu đề bài viết.' }]}
                >
                  <Input
                    size="large"
                    placeholder="Ví dụ: Top 10 giải pháp Machine Vision cho nhà máy"
                    maxLength={200}
                    showCount
                    style={{ fontWeight: 600 }}
                  />
                </Form.Item>

                <Form.Item
                  name="slug"
                  label="Đường dẫn"
                  tooltip="Để trống thì tự sinh từ tiêu đề. Đổi đường dẫn sau khi đăng sẽ làm gãy các link đã chia sẻ."
                  extra={`Địa chỉ trên website: ${websiteBlogBaseUrl}/${emptyToNull(slug) ?? '(tự sinh từ tiêu đề)'}`}
                >
                  <Input
                    prefix={<Typography.Text type="secondary">/blog/</Typography.Text>}
                    placeholder="tu-sinh-tu-tieu-de"
                  />
                </Form.Item>

                <Form.Item
                  name="excerpt"
                  label="Tóm tắt"
                  extra="Hiện trên thẻ bài viết và khi chia sẻ lên Facebook, Zalo. Bắt buộc khi đăng."
                >
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
                </Form.Item>

                <Form.Item label="Nội dung" style={{ marginBottom: 0 }}>
                  {isReady ? (
                    <RichTextEditor key={editorSeed} initialHtml={initialHtml} onChange={setContentHtml} />
                  ) : (
                    <div style={{ height: 480 }} />
                  )}
                </Form.Item>
              </Card>

              <div style={{ marginTop: 16 }}>
                <SeoPanel
                  report={seoReport}
                  effectiveTitle={effectiveTitle}
                  titleIsFallback={emptyToNull(watchedSeoTitle) === null}
                  effectiveDescription={effectiveDescription}
                  descriptionIsFallback={emptyToNull(watchedMetaDescription) === null}
                  slug={effectiveSlug}
                  imageUrl={shownCoverUrl}
                />
              </div>
            </Col>

            <Col xs={24} xl={8} xxl={7}>
              <div style={{ position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card size="small" title="Đăng bài">
                  <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                    {statusLine()}
                  </Typography.Paragraph>

                  <Form.Item
                    label="Ngày đăng"
                    extra="Để trống là đăng ngay. Chọn ngày giờ ở tương lai để hẹn giờ đăng."
                    style={{ marginBottom: 16 }}
                  >
                    <DatePicker
                      showTime={{ format: 'HH:mm' }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="Đăng ngay"
                      style={{ width: '100%' }}
                      value={publishAt}
                      onChange={(value: Dayjs | null) => { setPublishAt(value); }}
                    />
                  </Form.Item>

                  <Space direction="vertical" style={{ width: '100%' }}>
                    {isPublished ? (
                      <>
                        <Button
                          type="primary"
                          block
                          icon={<SendOutlined />}
                          loading={savingIntent === 'publish'}
                          disabled={isBusy && savingIntent !== 'publish'}
                          onClick={() => { void save('publish'); }}
                        >
                          Cập nhật bài đang đăng
                        </Button>
                        <Popconfirm
                          title="Gỡ bài khỏi website?"
                          description="Bài về lại dạng nháp, nội dung vẫn còn nguyên."
                          okText="Gỡ"
                          cancelText="Không"
                          onConfirm={() => { void save('unpublish'); }}
                        >
                          <Button block icon={<RollbackOutlined />} loading={savingIntent === 'unpublish'} disabled={isBusy}>
                            Gỡ về nháp
                          </Button>
                        </Popconfirm>
                      </>
                    ) : (
                      <>
                        <Button
                          type="primary"
                          block
                          icon={<SendOutlined />}
                          loading={savingIntent === 'publish'}
                          disabled={isBusy && savingIntent !== 'publish'}
                          onClick={() => { void save('publish'); }}
                        >
                          {isFuture ? 'Hẹn giờ đăng' : 'Đăng bài'}
                        </Button>
                        <Button
                          block
                          icon={<SaveOutlined />}
                          loading={savingIntent === 'save'}
                          disabled={isBusy && savingIntent !== 'save'}
                          onClick={() => { void save('save'); }}
                        >
                          Lưu nháp
                        </Button>
                      </>
                    )}

                    {post === null ? null : (
                      <Popconfirm
                        title="Xóa bài viết này?"
                        description="Bài sẽ biến mất khỏi website và khỏi danh sách."
                        okText="Xóa"
                        cancelText="Không"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => { void remove(); }}
                      >
                        <Button type="text" danger block icon={<DeleteOutlined />} disabled={isBusy}>
                          Xóa bài viết
                        </Button>
                      </Popconfirm>
                    )}
                  </Space>
                </Card>

                <Card
                  size="small"
                  title="Danh mục"
                  extra={
                    <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => { setIsCategoryOpen(true); }}>
                      Quản lý danh mục
                    </Button>
                  }
                >
                  <Form.Item name="categoryId" style={{ marginBottom: 0 }}>
                    <Select<string>
                      allowClear
                      placeholder="Chưa phân loại"
                      options={categories.map((category) => ({ value: category.id, label: category.name }))}
                    />
                  </Form.Item>
                </Card>

                <Card size="small" title="Ảnh bìa">
                  <Upload.Dragger
                    accept={blogImageExtensions.join(',')}
                    showUploadList={false}
                    fileList={[] as UploadFile[]}
                    beforeUpload={pickCover}
                  >
                    <div style={{ padding: '0 12px' }}>
                      {shownCoverUrl === null ? (
                        <div
                          style={{
                            aspectRatio: '16 / 9',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                          }}
                        >
                          <FileImageOutlined style={{ fontSize: 28, color: BrandColors.blue }} />
                          <Typography.Text>Kéo ảnh vào đây hoặc bấm để chọn</Typography.Text>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            JPG, PNG, WEBP, GIF — tối đa 5 MB
                          </Typography.Text>
                        </div>
                      ) : (
                        <img
                          src={shownCoverUrl}
                          alt="Ảnh bìa"
                          style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 6, display: 'block' }}
                        />
                      )}
                    </div>
                  </Upload.Dragger>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6, minHeight: 24 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                      {coverFile === null ? '' : `${coverFile.name} — gửi đi khi bấm lưu`}
                    </Typography.Text>
                    {shownCoverUrl === null ? null : (
                      <Button
                        danger
                        type="link"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setCoverFile(null);
                          setCoverPreview(null);
                          setIsCoverRemoved(true);
                        }}
                      >
                        Gỡ ảnh
                      </Button>
                    )}
                  </div>
                  <Form.Item
                    name="coverImageAlt"
                    label="Mô tả ảnh (alt)"
                    tooltip="Câu ngắn tả nội dung ảnh. Google Hình ảnh đọc phần này; để trống thì website dùng tiêu đề bài."
                    style={{ marginBottom: 0, marginTop: 8 }}
                  >
                    <Input placeholder="Ví dụ: Camera kiểm tra lỗi bề mặt linh kiện" maxLength={200} />
                  </Form.Item>
                </Card>

                <Card size="small" title="Tác giả">
                  <Form.Item name="authorName" style={{ marginBottom: 0 }}>
                    <Input placeholder="Ví dụ: Phòng Kỹ thuật MVA Lab" maxLength={100} />
                  </Form.Item>
                </Card>
              </div>
            </Col>
          </Row>
        </Form>

        <Modal
          open={isPreviewOpen}
          width="min(1280px, 96vw)"
          style={{ top: 16 }}
          footer={null}
          destroyOnHidden
          onCancel={() => { setIsPreviewOpen(false); }}
          title={
            <Space wrap>
              Xem trước trên website
              <Segmented<PreviewDevice>
                size="small"
                value={previewDevice}
                onChange={setPreviewDevice}
                options={[
                  { value: 'desktop', label: 'Máy tính', icon: <DesktopOutlined /> },
                  { value: 'mobile', label: 'Điện thoại', icon: <MobileOutlined /> },
                ]}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                Hiện cả phần chưa lưu
              </Typography.Text>
            </Space>
          }
        >
          {isPreviewOpen
            ? (() => {
                const article = draftArticle();

                return (
                  <WebsiteFrame url={`${websiteBlogBaseUrl}/${article.slug}`} device={previewDevice}>
                    <BlogArticlePreview
                      article={article}
                      totalCount={otherPosts.length + 1}
                      categories={previewCategories(article)}
                      recent={otherPosts.slice(0, 5)}
                      related={otherPosts
                        .filter((candidate) => candidate.categorySlug !== null && candidate.categorySlug === article.categorySlug)
                        .slice(0, 3)}
                      onBack={null}
                      onSelectCategory={() => {
                        // Cửa sổ này chỉ xem bài đang soạn; xem cả trang blog ở "Xem như trên website".
                      }}
                      onOpen={() => {
                        // Như trên — không mở bài khác từ đây.
                      }}
                    />
                  </WebsiteFrame>
                );
              })()
            : null}
        </Modal>

        <BlogCategoryModal
          open={isCategoryOpen}
          categories={categories}
          canManage={canManage}
          onClose={() => { setIsCategoryOpen(false); }}
          onChanged={() => { setCategoryToken((token) => token + 1); }}
        />
      </Space>
    </Spin>
  );
}
